// src/pages/Workforce/ShiftRosterPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import T from '../../components/common/T';
import { useAuth } from '../../context/AuthContext';
import { canManageWorkforce, getWorkforceId } from '../../utils/roleUtils';
import api from '../../api/axiosConfig';
import {
  CalendarDays, Sun, Sunrise, Moon, Search,
  RefreshCw, Plus, MapPin, Clock, Trash2,
  Calendar as CalendarIcon, ArrowLeft, Loader2, Check, Eye
} from "lucide-react";
import DataTable from '../../components/common/DataTables';
import PageLayout, { PageHeader, StatsGrid, Toolbar, TableCard } from '../../components/common/PageLayout';
import { TopPrimaryButton } from '../../components/common/StandardButtons';
import {
  CalendarDaysIcon,
  SunIcon,
  MoonIcon,
  ClockIcon,
  MapPinIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

// ─────────────────────────────────────────────────────────────────────────────
// Full-Page Assign Shift Form Component (Admin Only)
// ─────────────────────────────────────────────────────────────────────────────
function AssignShiftView({ onBack, onSuccess }) {
  const [form, setForm] = useState({
    employeeId: '',
    date:       '',
    shiftType:  'Morning',
    startTime:  '',
    endTime:    '',
    siteId:     '' 
  });
  const [employees,    setEmployees]    = useState([]);
  const [sites,        setSites]        = useState([]); 
  const [loadingEmps,  setLoadingEmps]  = useState(true);
  const [loadingSites, setLoadingSites] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError,  setSubmitError]  = useState(null);

  // Prefill sensible default times when shiftType changes
  useEffect(() => {
    const defaults = {
      Morning: { startTime: '06:00', endTime: '14:00' },
      Evening: { startTime: '14:00', endTime: '22:00' },
      Night:   { startTime: '22:00', endTime: '06:00' }
    };
    setForm(f => ({ ...f, ...defaults[f.shiftType] }));
  }, [form.shiftType]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const empRes = await api.get('/workforce');
        const empData = empRes.data?.data || empRes.data || [];
        setEmployees(Array.isArray(empData) ? empData : []);
        
        const siteRes = await api.get('/workforce/sites');
        const siteData = siteRes.data?.data || siteRes.data || [];
        setSites(Array.isArray(siteData) ? siteData : []);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoadingEmps(false);
        setLoadingSites(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async () => {
    if (!form.employeeId || !form.date || !form.startTime || !form.endTime || !form.siteId) {
      setSubmitError('Please fill in all required fields.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);

    // 🟢 FIX FOR THE "location cannot be null" ERROR: 
    // Find the site name from the dropdown selection and send it as the 'location'
    const selectedSite = sites.find(s => s.id.toString() === form.siteId.toString());
    const locationName = selectedSite ? selectedSite.site_name : 'Assigned Site';

    try {
      await api.post('/workforce/shifts', {
        employeeId: form.employeeId,
        date:       form.date,
        shiftType:  form.shiftType,
        startTime:  form.startTime,
        endTime:    form.endTime,
        site_id:    form.siteId, 
        location:   locationName // 🟢 This prevents the DB crash!
      });
      onSuccess();
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message || 'Failed to assign shift');
    } finally {
      setIsSubmitting(false);
    }
  };

  const shiftColors = {
    Morning: 'bg-amber-50  border-amber-200  text-amber-800',
    Evening: 'bg-orange-50 border-orange-200 text-orange-800',
    Night:   'bg-indigo-50 border-indigo-200 text-indigo-800'
  };

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-200">
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-gray-900 transition mb-6 w-fit">
        <ArrowLeft size={16} /> <T>Back to Roster</T>
      </button>

      <div className="bg-white dark:bg-[#161b22] rounded-xl shadow-xs border border-gray-200 dark:border-[#30363d] overflow-hidden max-w-3xl">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-[#30363d] bg-gray-50 dark:bg-[#21262d]">
          <h2 className="text-md font-bold text-gray-900 dark:text-white"><T>Assign New Shift</T></h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1"><T>Schedule an employee for a specific site and time window.</T></p>
        </div>

        <div className="p-6 sm:p-8 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Employee Selection */}
            <div>
              <label className="block text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-2"><T>Employee</T></label>
              {loadingEmps ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-slate-500 py-3 bg-gray-50 dark:bg-[#21262d] rounded-lg px-4"><Loader2 size={16} className="animate-spin" /> Loading...</div>
              ) : (
                <select value={form.employeeId} onChange={(e) => setForm(f => ({ ...f, employeeId: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-sm font-medium text-gray-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-blue-500 transition">
                  <option value="">— Select Employee —</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Date Selection */}
            <div>
              <label className="block text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-2"><T>Date</T></label>
              <input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-sm text-gray-700 dark:text-slate-200 font-medium outline-none focus:ring-2 focus:ring-blue-500 transition" />
            </div>
          </div>

          {/* Shift Type Selection */}
          <div className="pt-2">
            <label className="block text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-2"><T>Shift Type</T></label>
            <div className="flex flex-col sm:flex-row gap-3">
              {['Morning', 'Evening', 'Night'].map(type => (
                <button key={type} type="button" onClick={() => setForm(f => ({ ...f, shiftType: type }))} className={`flex-1 py-3 rounded-lg border text-sm font-bold transition ${form.shiftType === type ? shiftColors[type] : 'bg-white dark:bg-[#21262d] border-gray-200 dark:border-[#30363d] text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800'}`}>{type}</button>
              ))}
            </div>
          </div>

          {/* Time Windows */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
            <div>
              <label className="block text-xs font-bold uppercase text-gray-500 mb-2"><T>Start Time</T></label>
              <input type="time" value={form.startTime} onChange={(e) => setForm(f => ({ ...f, startTime: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 outline-none focus:ring-2 focus:ring-blue-500 transition" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-gray-500 mb-2"><T>End Time</T></label>
              <input type="time" value={form.endTime} onChange={(e) => setForm(f => ({ ...f, endTime: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 outline-none focus:ring-2 focus:ring-blue-500 transition" />
            </div>
          </div>

          {/* Site Selection */}
          <div className="pt-2">
            <label className="block text-xs font-bold uppercase text-gray-500 mb-2"><T>Assign to Site (Geofence Target)</T></label>
            {loadingSites ? (
               <div className="flex items-center gap-2 text-sm text-gray-400 py-3 bg-gray-50 rounded-lg px-4"><Loader2 size={16} className="animate-spin" /> Loading sites...</div>
            ) : (
              <select value={form.siteId} onChange={(e) => setForm(f => ({ ...f, siteId: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 outline-none focus:ring-2 focus:ring-blue-500 transition">
                <option value="">— Select Construction Site —</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>{site.site_name}</option>
                ))}
              </select>
            )}
          </div>

          {submitError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-lg flex items-center gap-2"><span className="font-bold">Error:</span> {submitError}</div>}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button onClick={onBack} className="px-6 py-2.5 rounded-lg border border-gray-300 text-sm font-bold text-gray-700 hover:bg-white transition bg-transparent">Cancel</button>
          <button onClick={handleSubmit} disabled={isSubmitting} className="flex items-center justify-center gap-2 px-8 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition shadow-sm disabled:opacity-60">
            {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
            {isSubmitting ? 'Assigning...' : 'Assign Shift'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page Component
// ─────────────────────────────────────────────────────────────────────────────
export default function ShiftRosterPage() {
  const { user } = useAuth();
  const isAdmin       = canManageWorkforce(user);
  const myWorkforceId = getWorkforceId(user);

  const [shifts,           setShifts]           = useState([]);
  const [isLoading,        setIsLoading]        = useState(true);
  const [error,            setError]            = useState(null);
  const [searchTerm,       setSearchTerm]       = useState("");
  const [shiftFilter,      setShiftFilter]      = useState("All");
  const [dateFilter,       setDateFilter]       = useState("");
  const [selectedShiftId,  setSelectedShiftId]  = useState(null);
  const [isRefreshing,     setIsRefreshing]     = useState(false);
  
  // View states: 'list', 'assign'
  const [currentView, setCurrentView] = useState('list');

  const selectedShift = shifts.find(s => s.id === selectedShiftId);

  const fetchShifts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const url = isAdmin
        ? '/workforce/shifts'
        : `/workforce/shifts/my?workforce_id=${myWorkforceId}`;

      const response = await api.get(url);
      const dbData = response.data?.data || response.data || [];
      const list = Array.isArray(dbData) ? dbData : [];
      setShifts(list.map(item => ({
        ...item,
        date:   new Date(item.date).toISOString().split('T')[0],
        avatar: item.employeeName ? item.employeeName.charAt(0).toUpperCase() : 'U'
      })));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [isAdmin, myWorkforceId]);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  const handleCancelShift = async (id) => {
    if (!window.confirm("Are you sure you want to cancel and delete this shift?")) return;
    const previousShifts = [...shifts];
    setShifts(prev => prev.filter(s => s.id !== id));
    setSelectedShiftId(null);
    try {
      await api.delete(`/workforce/shifts/${id}`);
    } catch (err) {
      setShifts(previousShifts);
      console.error("Deletion failed:", err);
    }
  };

  const handleRefresh = () => { setIsRefreshing(true); fetchShifts(); };

  const filteredData = useMemo(() => shifts.filter(shift => {
    const matchesSearch = isAdmin
      ? (shift.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         shift.id?.toLowerCase?.()?.includes(searchTerm.toLowerCase()))
      : true;
    const matchesShiftType = shiftFilter === "All" || shift.type === shiftFilter;
    const matchesDate      = dateFilter === "" || shift.date === dateFilter;
    return matchesSearch && matchesShiftType && matchesDate;
  }), [searchTerm, shiftFilter, dateFilter, shifts, isAdmin]);

  const stats = useMemo(() => ({
    total:   shifts.length,
    morning: shifts.filter(s => s.type === "Morning").length,
    evening: shifts.filter(s => s.type === "Evening").length,
    night:   shifts.filter(s => s.type === "Night").length
  }), [shifts]);

  const statsList = useMemo(() => [
    {
      label: isAdmin ? 'Total Scheduled' : 'My Shifts',
      value: stats.total,
      icon: CalendarDaysIcon,
      variant: 'default',
      isActive: shiftFilter === "All" || !shiftFilter,
      onClick: () => setShiftFilter("All")
    },
    {
      label: 'Morning',
      value: stats.morning,
      icon: SunIcon,
      variant: 'orange',
      isActive: shiftFilter === "Morning",
      onClick: () => setShiftFilter(s => s === "Morning" ? "All" : "Morning")
    },
    {
      label: 'Evening',
      value: stats.evening,
      icon: SunIcon,
      variant: 'blue',
      isActive: shiftFilter === "Evening",
      onClick: () => setShiftFilter(s => s === "Evening" ? "All" : "Evening")
    },
    {
      label: 'Night',
      value: stats.night,
      icon: MoonIcon,
      variant: 'purple',
      isActive: shiftFilter === "Night",
      onClick: () => setShiftFilter(s => s === "Night" ? "All" : "Night")
    }
  ], [isAdmin, stats, shiftFilter]);

  const getShiftStyles = (type) => {
    switch(type) {
      case 'Morning': return 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/60';
      case 'Evening': return 'bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800/60';
      case 'Night':   return 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800/60';
      default:        return 'bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700';
    }
  };

  const getShiftIcon = (type, className = "w-4 h-4") => {
    switch(type) {
      case 'Morning': return <Sun    className={className} />;
      case 'Evening': return <Sunrise className={className} />;
      case 'Night':   return <Moon   className={className} />;
      default:        return <Clock  className={className} />;
    }
  };

  const getShiftDetailBg = (type) => {
    switch(type) {
      case 'Morning': return 'bg-gradient-to-br from-amber-400  to-orange-500  text-white';
      case 'Evening': return 'bg-gradient-to-br from-orange-500 to-red-500     text-white';
      case 'Night':   return 'bg-gradient-to-br from-indigo-600 to-purple-700  text-white';
      default:        return 'bg-gradient-to-br from-gray-500   to-gray-700    text-white';
    }
  };

  // 🟢 DATATABLE COLUMNS: Shift Roster
  const shiftColumns = useMemo(() => {
    const cols = [
      {
        header: "Employee",
        accessor: "employeeName",
        render: (shift) => (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400 flex items-center justify-center font-bold text-sm shrink-0">
              {shift.avatar}
            </div>
            <div>
              <p className="font-bold text-gray-900 dark:text-white leading-tight">{shift.employeeName}</p>
              <p className="text-xs text-gray-500 dark:text-slate-400">{shift.role}</p>
            </div>
          </div>
        )
      },
      {
        header: "Date",
        accessor: "date",
        render: (shift) => (
          <span className="font-medium text-gray-700 dark:text-slate-300">
            {new Date(shift.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )
      },
      {
        header: "Shift Type",
        accessor: "type",
        render: (shift) => (
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase border tracking-wide ${getShiftStyles(shift.type)}`}>
            {getShiftIcon(shift.type, "w-4 h-4")}
            {shift.type}
          </div>
        )
      },
      { header: "Timing", accessor: "time", cellClassName: "font-mono text-gray-600 dark:text-slate-400" },
      { 
        header: "Location", 
        accessor: "location", 
        cellClassName: "text-gray-500 dark:text-slate-400 max-w-[200px] truncate",
        render: (shift) => <span title={shift.location}>{shift.location}</span>
      },
      {
        header: "Action",
        align: "center",
        render: (shift) => (
          <div className="flex items-center justify-center gap-2">
            <button 
              onClick={(e) => { e.stopPropagation(); setSelectedShiftId(shift.id); }} 
              className="p-1.5 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition" 
              title="View Full Details"
            >
              <Eye size={16} />
            </button>
            {isAdmin && (
              <button 
                onClick={(e) => { e.stopPropagation(); handleCancelShift(shift.id); }} 
                className="p-1.5 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-lg transition" 
                title="Cancel Shift"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )
      }
    ];
    return cols;
  }, [isAdmin]);

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER Logic
  // ─────────────────────────────────────────────────────────────────────────────
  if (currentView === 'assign' && isAdmin) {
    return (
      <PageLayout>
        <AssignShiftView 
          onBack={() => setCurrentView('list')} 
          onSuccess={async () => {
            setCurrentView('list');
            await fetchShifts();
          }} 
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <PageHeader
        icon={CalendarDaysIcon}
        iconColor="text-[#E8612C]"
        title={isAdmin ? 'Shift Roster' : 'My Upcoming Shifts'}
        subtitle={isAdmin ? 'Schedule and manage employee shift assignments' : 'Your scheduled shifts for the next 14 days'}
        onRefresh={handleRefresh}
        loading={isRefreshing || isLoading}
        actions={
          isAdmin && !selectedShift && (
            <TopPrimaryButton
              onClick={() => setCurrentView('assign')}
              icon={PlusIcon}
              label="Assign Shift"
            />
          )
        }
      />

      {/* ── ERROR BANNER ────────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 p-4 rounded-xl flex items-center justify-between shadow-2xs">
          <span><span className="font-bold">Error:</span> {error}</span>
          <button onClick={fetchShifts} className="px-3 py-1 bg-white dark:bg-[#161b22] border border-red-200 dark:border-red-900 rounded-lg text-xs font-bold hover:bg-red-50 transition">Try Again</button>
        </div>
      )}

      {/* ── DETAIL VIEW ──────────────────────────────────────────────────────── */}
      {selectedShift ? (
        <div className="animate-in fade-in duration-200 space-y-4">
          <button
            onClick={() => setSelectedShiftId(null)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <T>Back to {isAdmin ? 'Roster' : 'My Shifts'}</T>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-2xl p-6 shadow-2xs flex flex-col items-center text-center gap-4">
              <div className="w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400 flex items-center justify-center font-bold text-3xl">
                {selectedShift.avatar}
              </div>
              <div>
                <h3 className="text-md font-bold text-gray-900 dark:text-white">{selectedShift.employeeName}</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400">{selectedShift.role}</p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => handleCancelShift(selectedShift.id)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-950/30 transition mt-2"
                >
                  <TrashIcon className="w-4 h-4" /> Cancel Shift
                </button>
              )}
            </div>

            <div className={`rounded-2xl p-6 shadow-2xs lg:col-span-2 relative overflow-hidden ${getShiftDetailBg(selectedShift.type)}`}>
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
              <div className="absolute -right-4 -bottom-8 w-28 h-28 rounded-full bg-white/10" />

              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {getShiftIcon(selectedShift.type, "w-8 h-8")}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider opacity-70 mb-0.5">Shift ID: {selectedShift.id}</h4>
                      <h2 className="text-2xl font-bold">{selectedShift.type} Shift</h2>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold uppercase border border-white/30">
                    {selectedShift.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                  <div>
                    <span className="block opacity-70 font-bold uppercase text-[10px] tracking-wider mb-1">Assigned Date</span>
                    <div className="flex items-center gap-2 font-bold text-md">
                      <CalendarDaysIcon className="w-5 h-5 opacity-70" />
                      {new Date(selectedShift.date).toLocaleDateString(undefined, { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  <div>
                    <span className="block opacity-70 font-bold uppercase text-[10px] tracking-wider mb-1">Time Window</span>
                    <div className="flex items-center gap-2 font-bold text-md">
                      <ClockIcon className="w-5 h-5 opacity-70" />
                      {selectedShift.time}
                    </div>
                  </div>
                  <div className="sm:col-span-2 bg-white/20 p-4 rounded-xl border border-white/20">
                    <span className="block opacity-70 font-bold uppercase text-[10px] tracking-wider mb-1">Site / Location</span>
                    <div className="flex items-center gap-2 font-bold text-md">
                      <MapPinIcon className="w-5 h-5 opacity-70" />
                      {selectedShift.location}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ── MAIN LIST VIEW ───────────────────────────────────────────────────── */
        <div className="space-y-6">
          <StatsGrid stats={statsList} cols={4} />

          <Toolbar
            searchTerm={isAdmin ? searchTerm : undefined}
            onSearch={isAdmin ? setSearchTerm : undefined}
            searchPlaceholder="Search employee or shift ID..."
            tabs={['All', 'Morning', 'Evening', 'Night']}
            activeTab={shiftFilter}
            onTabChange={setShiftFilter}
          >
            <div className="relative w-full sm:w-auto">
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 bg-gray-50 dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-sm text-gray-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-[#E8612C] font-medium transition"
              />
            </div>
          </Toolbar>

          {!isAdmin && shifts.length === 0 && !isLoading && (
            <div className="bg-white dark:bg-[#161b22] rounded-2xl shadow-2xs border border-gray-200 dark:border-[#30363d] flex flex-col items-center justify-center py-20 text-center px-6">
              <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-400 flex items-center justify-center mb-4">
                <CalendarDays size={32} />
              </div>
              <p className="font-bold text-gray-700 dark:text-slate-200 text-base"><T>No Upcoming Shifts</T></p>
              <p className="text-sm text-gray-400 dark:text-slate-500 mt-1"><T>You have no shifts scheduled in the next 14 days.</T></p>
            </div>
          )}

          {(isAdmin || shifts.length > 0 || isLoading) && (
            <TableCard>
              <DataTable 
                data={filteredData} 
                columns={shiftColumns} 
                keyExtractor={(item) => item.id}
                emptyMessage="No shifts found for these filters."
                loading={isLoading}
              />
            </TableCard>
          )}
        </div>
      )}
    </PageLayout>
  );
}